import React, { useState } from 'react';
import { ArrowLeft, Save, Eye, EyeOff } from 'lucide-react';
import { ROLES, ROLE_LABELS, validateUserForm } from '../utils/userValidation';

const Field = ({ label, required, hint, error, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1.5">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      {hint && <span className="ml-1 font-normal text-gray-400">({hint})</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const inputCls = (error, disabled) =>
  `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-400 ${
    error    ? 'border-red-300 bg-white' :
    disabled ? 'border-gray-200 bg-gray-50 text-gray-400' :
               'border-gray-200 bg-white'
  }`;

export default function UserForm({ user = null, roles = [], onSubmit, onCancel }) {
  const isEditing = !!user;

  const [formData, setFormData] = useState(isEditing ? {
    username: user.username,
    password: '',
    nombre:   user.nombre,
    apellido: user.apellido,
    email:    user.email || '',
    rol:      user.rol,
    estado:   user.estado,
  } : {
    username: '', password: '', nombre: '', apellido: '',
    email: '', rol: 'employee', estado: 'activo',
  });

  const [errors, setErrors]           = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validation = validateUserForm(formData, isEditing);
    if (!validation.valid) { setErrors(validation.errors); return; }
    onSubmit(formData);
  };

  return (
    <div className="space-y-4">

      {/* ── Back button ─────────────────────────────────────────── */}
      <button
        type="button"
        onClick={onCancel}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
      >
        <ArrowLeft size={16} />
        Volver a Usuarios
      </button>

      {/* ── Title card ──────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl px-6 py-4">
        <h2 className="text-lg font-bold text-gray-900">
          {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
        </h2>
        {isEditing && (
          <p className="text-sm text-gray-400 mt-0.5">{user.username}</p>
        )}
      </div>

      {/* ── Form ────────────────────────────────────────────────── */}
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Credenciales */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Credenciales</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Usuario" required error={errors.username}>
              <input
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled={isEditing}
                placeholder="usuario123"
                className={inputCls(errors.username, isEditing)}
              />
            </Field>

            <Field label="Contraseña" required={!isEditing} hint={isEditing ? 'dejar vacío para no cambiar' : null} error={errors.password}>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={isEditing ? 'Opcional' : 'Mínimo 6 caracteres'}
                  className={inputCls(errors.password, false) + ' pr-9'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>
          </div>
        </div>

        {/* Datos personales */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Datos personales</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre" required error={errors.nombre}>
              <input name="nombre" value={formData.nombre} onChange={handleChange}
                placeholder="Juan" className={inputCls(errors.nombre)} />
            </Field>

            <Field label="Apellido" required error={errors.apellido}>
              <input name="apellido" value={formData.apellido} onChange={handleChange}
                placeholder="Pérez" className={inputCls(errors.apellido)} />
            </Field>

            <Field label="Email" error={errors.email}>
              <input type="email" name="email" value={formData.email} onChange={handleChange}
                placeholder="usuario@7family.com" className={inputCls(errors.email)} />
            </Field>

            <Field label="Rol" required>
              <select name="rol" value={formData.rol} onChange={handleChange} className={inputCls()}>
                {roles.length > 0
                  ? roles.map(r => <option key={r.id} value={r.codigo}>{r.nombre}</option>)
                  : Object.entries(ROLE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))
                }
              </select>
            </Field>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={14} />
            {isEditing ? 'Guardar cambios' : 'Crear usuario'}
          </button>
        </div>
      </form>
    </div>
  );
}
